import styled from "styled-components";

import { colorsPalettes, setElevation } from "@similarweb/styles";
import { ChangeChip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import type { HeatmapTopic } from "app/pages/market-research/api-client/topicsHeatmapAPI";
import { TopicsDiscoverTrendCardChart } from "app/pages/market-research/features/topics-discover/TopicsDiscoverTrendCardChart";
import { allTrackers } from "app/services/track/track";

import { changeFormatter, getGrowthSentiment, navigateToTopic, volumeFormatter } from "./utils";

const RowContainer = styled(Flex)`
    height: 68px;
    padding: 8px 16px;
    border-radius: 6px;
    border: 1px solid ${colorsPalettes.carbon[50]};
    background: ${colorsPalettes.carbon[0]};
    cursor: pointer;
    transition:
        box-shadow 300ms ease-in-out,
        border-color 300ms ease-in-out;

    &:hover {
        ${setElevation("Z1")}
        border-color: ${colorsPalettes.carbon[100]};
    }
`;

const TopicName = styled(Text)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

type TopicHighlightRowProps = {
    topic: HeatmapTopic;
};

export const TopicHighlightRow = ({ topic }: TopicHighlightRowProps) => {
    const { name, volume, volumeChange, trend } = topic;

    const handleClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", `Topics Heatmap Card/${name}`);
        navigateToTopic(name);
    };

    return (
        <RowContainer
            alignItems="center"
            gap="16px"
            onClick={handleClick}
            data-automation={`heatmap-topic-row-${name}`}
        >
            <Flex
                flex="1"
                minWidth="160px"
            >
                <TopicName
                    fontVariant="subtitle1"
                    color={colorsPalettes.carbon[500]}
                >
                    {name}
                </TopicName>
            </Flex>
            <Flex
                gap="8px"
                alignItems="center"
            >
                <Text
                    fontVariant="h5"
                    color={colorsPalettes.carbon[400]}
                >
                    {volumeFormatter(volume)}
                </Text>
                <Flex
                    width="80px"
                    justifyContent="center"
                >
                    <ChangeChip
                        sentiment={getGrowthSentiment(volumeChange?.growth)}
                        size="small"
                    >
                        {changeFormatter(volumeChange?.change)}
                    </ChangeChip>
                </Flex>
            </Flex>
            <Flex
                flex="1"
                alignItems="center"
                maxWidth="160px"
                minWidth="80px"
            >
                <Box width="100%">
                    <TopicsDiscoverTrendCardChart trendData={trend} />
                </Box>
            </Flex>
        </RowContainer>
    );
};
